import { profileLocal } from './profiles/local';

/**
 * Stickers on the profile's name card: emoji the person sticks on it in "Edit" mode, moves and peels
 * off. Places are fractions of the card, so the card can change width. Kept in the profile (key
 * `vp-profile-stickers`, rides along in `.vpprofile`), with `sync` / `rev` like the tray layout, for a
 * future sync. Only cosmetic: nothing else reads them.
 */

export const PROFILE_STICKERS_KEY = 'vp-profile-stickers';
export const MAX_STICKERS = 12;

/** What the palette offers. Emoji for now; a sticker keeps its text, so other sets can come later. */
export const STICKER_SET = ['😀', '😎', '🥳', '🤩', '😺', '🦊', '🐼', '🐙', '🦄', '🌈', '⭐', '🔥', '⚡', '🌸', '🍀', '🍕', '☕', '🎧', '🎨', '🚀', '💡', '🎯', '🏆', '❤️'];

export interface Sticker {
  id: string;
  /** The emoji. */
  e: string;
  /** Centre, fractions of the card (0..1). */
  x: number;
  y: number;
  /** Tilt, degrees. */
  r: number;
}

export interface StickerSheet {
  v: 1;
  sync: 'pending' | 'acked';
  rev: number;
  items: Sticker[];
}

const empty = (): StickerSheet => ({ v: 1, sync: 'acked', rev: 0, items: [] });
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

function parse(raw: unknown): StickerSheet {
  if (!raw || typeof raw !== 'object' || (raw as { v?: unknown }).v !== 1) return empty();
  const data = raw as { sync?: unknown; rev?: unknown; items?: unknown };
  const items: Sticker[] = [];
  for (const s of Array.isArray(data.items) ? data.items : []) {
    if (!s || typeof s !== 'object') continue;
    const it = s as Partial<Sticker>;
    if (typeof it.id !== 'string' || typeof it.e !== 'string' || !it.e || it.e.length > 16 || !finite(it.x) || !finite(it.y)) continue;
    items.push({ id: it.id, e: it.e, x: clamp01(it.x), y: clamp01(it.y), r: finite(it.r) ? Math.max(-45, Math.min(45, it.r)) : 0 });
    if (items.length >= MAX_STICKERS) break;
  }
  return { v: 1, sync: data.sync === 'pending' ? 'pending' : 'acked', rev: finite(data.rev) ? data.rev : 0, items };
}

export function readStickers(): StickerSheet {
  try {
    return parse(JSON.parse(profileLocal.getItem(PROFILE_STICKERS_KEY) ?? 'null'));
  } catch {
    return empty();
  }
}

/** Every change is a new revision waiting for a future sync. */
export function writeStickers(sheet: StickerSheet): StickerSheet {
  const next: StickerSheet = { v: 1, sync: 'pending', rev: sheet.rev + 1, items: sheet.items.map((s) => ({ ...s })) };
  try {
    profileLocal.setItem(PROFILE_STICKERS_KEY, JSON.stringify(next));
  } catch {
    /* storage full or blocked: this visit still shows them */
  }
  return next;
}

/** A new sticker somewhere on the card with a little tilt; nothing is added past the limit. */
export function addSticker(sheet: StickerSheet, e: string, rand: () => number = Math.random): StickerSheet {
  if (sheet.items.length >= MAX_STICKERS) return sheet;
  const id = `s${Date.now().toString(36)}${Math.floor(rand() * 1e6).toString(36)}`;
  const item: Sticker = { id, e, x: 0.35 + rand() * 0.35, y: 0.15 + rand() * 0.7, r: Math.round((rand() - 0.5) * 40) };
  return { ...sheet, items: [...sheet.items, item] };
}

export function moveSticker(sheet: StickerSheet, id: string, x: number, y: number): StickerSheet {
  return { ...sheet, items: sheet.items.map((s) => (s.id === id ? { ...s, x: clamp01(x), y: clamp01(y) } : s)) };
}

export function removeSticker(sheet: StickerSheet, id: string): StickerSheet {
  return { ...sheet, items: sheet.items.filter((s) => s.id !== id) };
}

/** A future sync acknowledges the stickers it sent. */
export function ackStickersSync(): void {
  const sheet = readStickers();
  if (sheet.sync === 'acked') return;
  try {
    profileLocal.setItem(PROFILE_STICKERS_KEY, JSON.stringify({ ...sheet, sync: 'acked' }));
  } catch {
    /* nothing to do */
  }
}
